/*
|-------------------------------------------------------------------------------
| VUEX modules/truckRunnings.js
|-------------------------------------------------------------------------------
| The Vuex data store for the cafes

status = 0 -> data hasn't been loaded
status = 1 -> data is loading
status = 2 -> data is loaded successfully
status = 3 -> data is loaded unsuccessfully
*/

import TruckRunningJourneyAPI from '../api/truckRunningJourney.js';
import SchedulesKpiReportExporter from '../api/schedulesKpiReportExporter'

export const truckRunningJourneys = {
    state: {
        truckRunningJourneys: [],
        truckRunningJourneysLoadStatus: 0,
        dayRange: 0,
        schedulesKpiReportExportStatus: 0,
    },

    actions: {
        loadTruckRunningJourneys ( { commit, state }, { mode = null } = {} ){
            return new Promise((resolve, reject) => {
                commit( 'setTruckRunningJourneysLoadStatus', 1 );

                TruckRunningJourneyAPI.get( { monitored: state.dayRange === 0 ? 1 : 0, dayRange: state.dayRange, mode: mode } )
                    .then( function( response ){
                        const newJourneys = response.data.data.map(res => mapToJourney(res));
                        commit( 'setTruckRunningJourneys', newJourneys);
                        commit( 'setTruckRunningJourneysLoadStatus', 2 );
                        resolve();
                    })
                    .catch( function(error){
                        commit( 'setTruckRunningJourneys', [] );
                        commit( 'setTruckRunningJourneysLoadStatus', 3 );
                        reject();
                    });
            });
        },
        loadTruckRunningJourneysTv ( { commit, state }, { modeCode } = {} ){
            return new Promise((resolve, reject) => {
                commit( 'setTruckRunningJourneysLoadStatus', 1 );

                TruckRunningJourneyAPI.get( { monitored: 1, modeCode: modeCode, mode: 'tv' } )
                    .then( function( response ){
                        const newJourneys = response.data.data.map(res => mapToJourney(res));
                        commit( 'setTruckRunningJourneys', newJourneys);
                        commit( 'setTruckRunningJourneysLoadStatus', 2 );
                        resolve();
                    })
                    .catch( function(error){
                        commit( 'setTruckRunningJourneys', [] );
                        commit( 'setTruckRunningJourneysLoadStatus', 3 );
                        reject();
                    });
            });
        },
        updateDayRange ( { commit }, dayRange ){
            commit( 'setDayRange', dayRange );
        },
        exportSchedulesKpiReport ({ commit }, params ){
            return new Promise((resolve, reject) => {
                commit('setSchedulesKpiReportExportStatus', 1);
                SchedulesKpiReportExporter.get( params )
                    .then( function( response ){
                        commit( 'setSchedulesKpiReportExportStatus', 2 );
                        resolve(response);
                    })
                    .catch( function(error){
                        commit( 'setSchedulesKpiReportExportStatus', 3 );
                        reject(error);
                    });
            })
        },
    },

    mutations: {
        setTruckRunningJourneys( state, journeys ){
            state.truckRunningJourneys = journeys;
        },
        setTruckRunningJourneysLoadStatus( state, status ){
            state.truckRunningJourneysLoadStatus = status;
        },
        setSchedulesKpiReportExportStatus( state, status ){
            state.schedulesKpiReportExportStatus = status;
        },
        setDayRange ( state, dayRange ){
            state.dayRange = dayRange;
        }
    },

    getters: {
        getTruckRunningJourneysLoadStatus( state ){
            return state.truckRunningJourneysLoadStatus;
        },
        getTruckRunningJourneys( state ){
            return state.truckRunningJourneys;
        },
        getDayRange( state ){
            return state.dayRange;
        },
        getSchedulesKpiReportExportStatus( state, status ){
            return state.schedulesKpiReportExportStatus;
        },
    },
};

/**
 * Helpers
 */
function mapToJourney (resource) {
    //the raw response must have std and sta, but may have atd, eta and ata
    const std = moment(resource.std);
    const atd = resource.atd ? moment(resource.atd) : null;
    const sta = moment(resource.sta);
    const eta = resource.eta ? moment(resource.eta) : null;
    const ata = resource.ata ? moment(resource.ata) : null;

    let truckPlate = resource.truck_running ? resource.truck_running.plate  : 'Unknown Plate';
    let status = '';
    switch (resource.status) {
        case 0:
            status = 'Scheduled';
            truckPlate = resource.monitored ? 'No Truck Detected' : '';
            break;
        case 1:
            status = 'Expected to arrive';
            break;
        case 2:
            status = 'Arrived';
            break;
        case -1:
            status = 'Cancelled';
            truckPlate = 'None';
            break;
        default:
            status = '';
    }

    //departure
    let departureDelay = null;
    if (resource.status > 0 && atd !== null) {
        departureDelay = atd.diff(std, 'minutes')
    }
    
    let atdClass = '';
    if (departureDelay < 5) {
        atdClass = 'text-on-time';
    }
    if (departureDelay >= 5 && departureDelay <= 30) {
        atdClass = 'text-delay-yellow';
    }
    if (departureDelay > 30) {
        atdClass = 'text-delay-red';
    }

    //arrival & status
    let statusTiming = '';
    let etaOrAtaClass = [];
    let statusClasses = ['status-cell'];
    let arrivalDelay = null;
    let etaOrAta = ata ? ata : (eta ? eta : null);
    let middleWords = '';
        //departed and has eta
    if (resource.status === 1 && eta !== null) {
        arrivalDelay = eta.diff(sta, 'minutes');
        etaOrAtaClass.push('text-eta');
        middleWords = ' at least'
    }
        //arrived and has ata
    if (resource.status === 2 && ata !== null) {
        arrivalDelay = ata.diff(sta, 'minutes');
    }
    if (! isNaN(Number.parseInt(arrivalDelay))) {
        if (arrivalDelay < 1) {
            statusTiming = ' on time';
            etaOrAtaClass.push('text-on-time');
            statusClasses.push('on-time');
        }
        if (arrivalDelay === 1) {
            statusTiming = `${middleWords} 1 minute late`;
            etaOrAtaClass.push('text-on-time');
            statusClasses.push('on-time');
        }
        if (arrivalDelay > 1 && arrivalDelay < 5) {
            statusTiming = `${middleWords} ${arrivalDelay} minutes late`;
            etaOrAtaClass.push('text-on-time');
            statusClasses.push('on-time');
        }
        if (arrivalDelay >= 5 && arrivalDelay <= 30) {
            statusTiming = `${middleWords} ${arrivalDelay} minutes late`;
            etaOrAtaClass.push('text-delay-yellow');
            statusClasses.push('delay-yellow');
        }
        if (arrivalDelay > 30) {
            statusTiming = `${middleWords} ${etaOrAta.from(sta, true)} late`;
            etaOrAtaClass.push('text-delay-red');
            statusClasses.push('delay-red');
        }
    }

    //running truck latest location
    let latestLocation = '';
    if (resource.latest_point !== undefined && resource.latest_point !== '' ) {
        latestLocation = {
            courseOverGround: resource.latest_point.course_over_ground,
            coordinates: resource.latest_point.coordinates,
            time: moment(resource.latest_point.time + '+0000'),
            speed: resource.latest_point.speed,
            location: resource.latest_point.location,
        }
    }

    return {
        id: resource.id,
        originPlace: resource.place_origin,
        std: std,
        atd: atd,
        destinationPlace: resource.place_destination,
        sta: sta,
        etaOrAta: etaOrAta,
        truckPlate: truckPlate,
        status: status + statusTiming,
        latestPoint: latestLocation,
        atdClass: atdClass,
        etaOrAtaClass: etaOrAtaClass,
        statusClasses,
        name: resource.name,
        contractor: resource.contractor,
    }
}